import { useState } from "react";
import { FileText, ExternalLink } from "lucide-react";
import { useMyAuditResults } from "@/lib/auditImportQueries";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Master v5 Prompt 29 — client-facing "Audit Result Report". Self-scoped
 * (server resolves the caller's own membership only — see
 * `list_client_audit_results`); only ever shows imports a coach has
 * already accepted/partially accepted (never pending_review/dismissed).
 * Used in FilesPage (individual Workspaces — Files IS the client's
 * personal area there) and MyWorkPage (team Workspaces — never surfaced
 * in the shared Our Work page).
 */
export function MyAuditResults({ workspaceId, emptyState = null }) {
  const { data, isPending } = useMyAuditResults(workspaceId);
  const [openResultId, setOpenResultId] = useState(null);

  if (isPending) return null;
  const items = data?.items || [];
  const openResult = items.find((r) => r.result_id === openResultId);

  if (items.length === 0) return emptyState;

  return (
    <div className="border border-terracotta/30 bg-terracotta/5 rounded-lg p-5 mb-6" data-testid="my-audit-results-section">
      <div className="flex items-center gap-2 text-terracotta mb-3"><FileText size={16} /><span className="text-sm uppercase tracking-wide font-medium">Your Audit Result Report</span></div>
      <ul className="space-y-2">
        {items.map((r) => (
          <li key={r.result_id} className="flex items-center justify-between gap-3 text-sm" data-testid={`my-audit-result-${r.result_id}`}>
            <div>
              <p className="text-ink font-medium">{r.report?.emailSubject || "Your High-Stakes Audit result"}</p>
              <p className="text-xs text-ink-soft">Completed {new Date(r.completed_at).toLocaleDateString()}</p>
            </div>
            <button
              onClick={() => setOpenResultId(r.result_id)}
              className="inline-flex items-center gap-1 text-xs font-medium text-terracotta hover:text-terracotta/80 shrink-0"
              data-testid={`my-audit-result-open-${r.result_id}`}
            >
              View report <ExternalLink size={12} />
            </button>
          </li>
        ))}
      </ul>

      <Dialog open={!!openResult} onOpenChange={(o) => !o && setOpenResultId(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="my-audit-result-dialog">
          {openResult && (
            <>
              <DialogHeader>
                <DialogTitle>{openResult.report?.emailSubject || "Your Audit Result Report"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                {(openResult.report?.sections || []).map((s) => (
                  <div key={s.sectionId} data-testid={`my-audit-result-section-${s.sectionId}`}>
                    <p className="text-sm font-medium text-ink">{s.heading}</p>
                    <p className="text-sm text-ink-soft mt-0.5 whitespace-pre-wrap">{s.body}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
